import { DatePicker } from '@/components/commons/date-picker-custom';
import { SignupElementDataType } from '@/components/sections/builder/elements/body/signup/type';
import { ElementProps } from '@/components/sections/builder/types';
import { Button } from '@/components/ui/button';
import { FormItem } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/use-toast';
import { signup } from '@/lib/actions/customer/action';
import { SendEventMetapixel } from '@/lib/analytic-send-event';
import { cn, isEmpty } from '@/lib/utils';
import { format } from 'date-fns';
import { Form, Formik } from 'formik';
import { Eye, EyeClosed } from 'lucide-react';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import React from 'react';

import * as Yup from 'yup';

const SignupSchema = Yup.object().shape({
  first_name: Yup.string()
    .matches(
      /^[A-Za-z\s]+$/,
      'First Name and Last Name cannot contain numbers or special characters. Only letters and spaces are allowed.'
    )
    .min(3, 'First name must be at least 3 characters')
    .max(50, 'First name cannot be longer than 50 characters')
    .required('First name is a required'),
  last_name: Yup.string()
    .matches(
      /^[A-Za-z\s]+$/,
      'First Name and Last Name cannot contain numbers or special characters. Only letters and spaces are allowed.'
    )
    .min(3, 'Last name must be at least 3 characters')
    .max(50, 'Last name cannot be longer than 50 characters')
    .optional(),
  email: Yup.string().email('Invalid Format Email').required("Email can't be blank"),
  password: Yup.string()
    .min(8, 'Password must be at least 8 characters')
    .max(64, 'Password cannot be longer than 64 characters')
    .required("Password can't be blank"),
  password_confirmation: Yup.string()
    .oneOf([Yup.ref('password')], 'Passwords must match')
    .required('Password confirmation is required'),
});

const initialValues = {
  first_name: '',
  last_name: '',
  dob: new Date(),
  email: '',
  password: '',
  password_confirmation: '',
};

export default function ElementContentSignup({ element, host }: ElementProps) {
  const router = useRouter();
  const query = useSearchParams();
  const isPreview = query.get('isPreview') || false;
  const token = query.get('token') || '';
  const themeId = query.get('theme_id') || '';
  const checkout_url = query.get('checkout_url');

  const [isSubmitted, setIsSubmitted] = React.useState<boolean>(false);
  const [showPassword, setShowPassword] = React.useState<boolean>(false);
  const [showPasswordConfirmation, setShowPasswordConfirmation] = React.useState<boolean>(false);

  const content = element.data as SignupElementDataType;

  const handleOnSubmit = async (values: typeof initialValues) => {
    if (isPreview || !!token) return;

    try {
      setIsSubmitted(true);

      const payload = {
        ...values,
        dob: format(values.dob, 'yyyy-MM-dd'),
      };
      const result = await signup(host as string, payload);

      if (result.success) {
        toast({
          title: 'Success',
          description:
            result?.message ||
            'Sign up successfully, please check your email to verify your account.',
          variant: 'success',
        });

        SendEventMetapixel('CompleteRegistration', {
          currency: 'IDR',
          content_type: 'customer',
          value: 0,
          quantity: 1,
          contents: [
            {
              customer_email: payload.email,
              customer_name: `${payload.first_name} ${payload.last_name}`,
              customer_dob: payload.dob,
            },
          ],
        });

        if (checkout_url) {
          router.replace(`/account/signin?checkout_url=${encodeURIComponent(checkout_url)}`);
        } else {
          router.replace('/account/signin');
        }
      } else {
        toast({
          title: 'Error',
          description: result?.message || 'Failed to sign up',
          variant: 'destructive',
        });
      }
    } catch (error) {
      if (error instanceof Error) {
        toast({
          title: 'Error',
          description: error.message || 'Something went wrong.',
          variant: 'destructive',
        });
      }
    } finally {
      setIsSubmitted(false);
    }
  };

  const handleShowPassword = (field: string) => {
    if (field === 'password') {
      setShowPassword((prev) => !prev);
    } else {
      setShowPasswordConfirmation((prev) => !prev);
    }
  };

  return (
    <>
      <div className='signup_padding bg_scheme_signout flex h-full min-h-[20vh] w-full items-center justify-center'>
        <div className='flex w-full max-w-lg flex-col items-center'>
          <div className='flex w-full items-start'>
            <h1 className='mb-7 text-2xl font-medium'>Create account</h1>
          </div>
          <div className='w-full'>
            <Formik
              key={JSON.stringify(initialValues)}
              initialValues={initialValues}
              validationSchema={SignupSchema}
              onSubmit={handleOnSubmit}
            >
              {({ values, errors, handleSubmit, handleChange, handleBlur, touched }) => (
                <Form onSubmit={handleSubmit} className='flex flex-col gap-6'>
                  <FormItem>
                    <div className='flex w-full flex-col'>
                      <Label htmlFor='first_name' className='mb-2 font-normal'>
                        First name
                      </Label>
                      <Input
                        name='first_name'
                        type='text'
                        className='font-normal'
                        placeholder='Enter your first name'
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.first_name}
                        required
                      />
                      {touched.first_name && errors.first_name && (
                        <span className='mt-1 text-xs text-red-600'>{errors.first_name}</span>
                      )}
                    </div>
                  </FormItem>
                  <FormItem>
                    <div className='flex w-full flex-col'>
                      <Label htmlFor='last_name' className='mb-2 font-normal'>
                        Last name
                      </Label>
                      <Input
                        name='last_name'
                        type='text'
                        className='font-normal'
                        placeholder='Enter your last name'
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.last_name}
                      />
                      {touched.last_name && errors.last_name && (
                        <span className='mt-1 text-xs text-red-600'>{errors.last_name}</span>
                      )}
                    </div>
                  </FormItem>
                  <FormItem className='flex flex-col'>
                    <Label htmlFor='dob' className='font-normal'>
                      Date of birth
                    </Label>
                    <DatePicker
                      value={values.dob}
                      onChange={(value) => {
                        handleChange({
                          target: {
                            name: 'dob',
                            value,
                          },
                        });
                      }}
                    />
                  </FormItem>
                  <FormItem>
                    <div className='flex w-full flex-col'>
                      <Label htmlFor='email' className='mb-2 font-normal'>
                        Email
                      </Label>
                      <Input
                        name='email'
                        type='email'
                        className='font-normal'
                        classNameWrapper='w-full'
                        value={values.email}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder='Enter your email address'
                        required
                      />
                      {errors.email && touched.email && (
                        <span className='mt-1 text-red-500'>{errors.email}</span>
                      )}
                    </div>
                  </FormItem>
                  <FormItem>
                    <div className='flex w-full flex-col'>
                      <Label htmlFor='password' className='mb-2 font-normal'>
                        Password
                      </Label>
                      <div className='relative'>
                        <Input
                          name='password'
                          type={showPassword ? 'text' : 'password'}
                          className='font-normal'
                          classNameWrapper='w-full'
                          value={values.password}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          placeholder='Create your password'
                          required
                        />
                        <button
                          type='button'
                          onClick={() => handleShowPassword('password')}
                          className='absolute right-3 top-1/2 -translate-y-1/2 transform text-gray-500'
                        >
                          {showPassword ? (
                            <EyeClosed className='h-4 w-4' />
                          ) : (
                            <Eye className='h-4 w-4' />
                          )}
                        </button>
                      </div>
                      {errors.password && touched.password && (
                        <span className='mt-1 text-red-500'>{errors.password}</span>
                      )}
                    </div>
                  </FormItem>
                  <FormItem>
                    <div className='flex w-full flex-col'>
                      <Label htmlFor='password_confirmation' className='mb-2 font-normal'>
                        Confirm password
                      </Label>
                      <div className='relative'>
                        <Input
                          name='password_confirmation'
                          type={showPasswordConfirmation ? 'text' : 'password'}
                          className='font-normal'
                          value={values.password_confirmation}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          placeholder='Enter your password again'
                          classNameWrapper='w-full'
                          required
                        />
                        <button
                          type='button'
                          onClick={() => handleShowPassword('password_confirmation')}
                          className='absolute right-3 top-1/2 -translate-y-1/2 transform text-gray-500'
                        >
                          {showPasswordConfirmation ? (
                            <EyeClosed className='h-4 w-4' />
                          ) : (
                            <Eye className='h-4 w-4' />
                          )}
                        </button>
                      </div>
                      {errors.password_confirmation && touched.password_confirmation && (
                        <span className='mt-1 text-red-500'>{errors.password_confirmation}</span>
                      )}
                    </div>
                  </FormItem>
                  <p
                    className='text-sm font-normal'
                    style={{ color: content.colorScheme?.textSecondary }}
                  >
                    Your personal data will be used to support your experience throughout this
                    website, to manage access to your account, and for other purposes described in{' '}
                    <b>our privacy policy</b>.
                  </p>
                  <Button
                    className={cn('theme_button mt-2 w-full disabled:hover:cursor-not-allowed')}
                    type='submit'
                    style={
                      !isEmpty(content.colorScheme)
                        ? {
                            backgroundColor: content.colorScheme?.btnSolidBackgroud,
                            color: content?.colorScheme?.btnSolidlabel,
                          }
                        : {}
                    }
                    disabled={(isPreview as boolean) || isSubmitted || !!token}
                  >
                    <span className='text-sm'>{isSubmitted ? 'Please wait...' : 'Sign up'}</span>
                  </Button>
                </Form>
              )}
            </Formik>
            <div className='mt-6 items-center text-center text-sm'>
              Already have an account?{' '}
              <Link
                href={`/account/signin${token ? `?theme_id=${themeId}&token=${token}` : ''}`}
                className='inline-flex items-center text-sm'
                style={
                  !isEmpty(content.colorScheme)
                    ? {
                        color: content?.colorScheme?.btnSolidBackgroud,
                      }
                    : {}
                }
              >
                Sign in here
              </Link>
            </div>
          </div>
        </div>
      </div>
      <style jsx>
        {`
          .bg_scheme_signout {
            background-color: ${content.colorScheme?.background || 'white'};
            color: ${content.colorScheme?.text || '#52535B'};
          }
          .signup_padding {
            padding-top: ${content.topPadding || 0}px;
            padding-bottom: ${content.bottomPadding || 0}px;
          }
        `}
      </style>
    </>
  );
}
